import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, ExternalLink, Loader2, ShoppingBag } from "lucide-react";
import { toast } from "react-toastify";
import { metaCatalogConnectionApi } from "../../api/metaCatalogConnectionApi";
import MetaCatalogProductsPanel from "./MetaCatalogProductsPanel";

const initialStatus = {
  connected: false,
  connectionStatus: "not_connected",
  wabaAssociationStatus: "association_unverified",
  reconnectRequired: false,
  version: 0,
};

const recoverableConnectionCodes = new Set([
  "META_CATALOG_PERMISSION_REQUIRED",
  "META_CATALOG_ACCESS_REVOKED",
]);

const associationErrorCodes = new Set([
  "META_CATALOG_WABA_ASSOCIATION_REQUIRED",
  "META_CATALOG_WABA_ASSOCIATION_UNAVAILABLE",
]);

export default function MetaCatalogConnectionPanel({ canConnect, canDisconnect }) {
  const [status, setStatus] = useState(initialStatus);
  const [catalogues, setCatalogues] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [connectionErrorCode, setConnectionErrorCode] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [showProducts, setShowProducts] = useState(false);

  const requiresReconnect =
    recoverableConnectionCodes.has(connectionErrorCode) || status.reconnectRequired;
  const needsSelection =
    status.connectionStatus === "authorized" && !requiresReconnect;
  const associationWarning =
    status.connected && status.wabaAssociationStatus !== "verified";
  const associationError = associationErrorCodes.has(connectionErrorCode);

  const loadCatalogues = useCallback(async () => {
    setBusy("catalogues");
    setError("");
    setConnectionErrorCode("");
    try {
      const result = await metaCatalogConnectionApi.listCatalogues();
      setCatalogues(Array.isArray(result) ? result : []);
      setSelectedId(current => current || result?.[0]?.id || "");
    } catch (requestError) {
      setError(requestError.message);
      setConnectionErrorCode(requestError.code || "");
    } finally {
      setBusy("");
    }
  }, []);

  const loadStatus = useCallback(async () => {
    setError("");
    try {
      const result = await metaCatalogConnectionApi.getStatus();
      setStatus({ ...initialStatus, ...result });
      setConnectionErrorCode(result?.errorCode || "");
      if (result?.connectionStatus === "authorized") await loadCatalogues();
    } catch (requestError) {
      setError(requestError.message);
      setConnectionErrorCode(requestError.code || "");
    } finally {
      setLoaded(true);
    }
  }, [loadCatalogues]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("metaCatalogAuth") === "failed") {
      setError("Meta catalogue authorization was not completed.");
    }
  }, []);

  const connect = async () => {
    setBusy("connect");
    setError("");
    try {
      const result = await metaCatalogConnectionApi.startAuthorization(
        window.location.pathname,
      );
      window.location.assign(result.authorizationUrl);
    } catch (requestError) {
      setError(requestError.message);
      setBusy("");
    }
  };

  const select = async () => {
    if (!selectedId || busy) return;
    setBusy("select");
    setError("");
    setConnectionErrorCode("");
    try {
      const result = await metaCatalogConnectionApi.selectCatalogue(
        selectedId,
        status.version,
      );
      setStatus({ ...initialStatus, ...result });
      toast.success("Meta catalogue connected.");
    } catch (requestError) {
      setError(requestError.message);
      setConnectionErrorCode(requestError.code || "");
    } finally {
      setBusy("");
    }
  };

  const disconnect = async () => {
    if (busy || !window.confirm("Disconnect this Meta catalogue from XploreByte?")) return;
    setBusy("disconnect");
    setError("");
    try {
      const result = await metaCatalogConnectionApi.disconnect(status.version);
      setStatus({ ...initialStatus, ...result });
      setCatalogues([]);
      setSelectedId("");
      toast.success("Meta catalogue disconnected.");
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy("");
    }
  };

  const lastVerified = useMemo(
    () =>
      status.lastVerifiedUtc
        ? new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(
            new Date(status.lastVerifiedUtc),
          )
        : "Not verified",
    [status.lastVerifiedUtc],
  );

  return (
    <section className="mb-4 rounded-lg border border-slate-200 bg-white px-4 py-3" aria-label="Native WhatsApp Commerce">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-emerald-50 text-emerald-700">
            <ShoppingBag size={17} />
          </span>
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-slate-950">Native WhatsApp Commerce</h2>
            {!status.connected && !needsSelection ? (
              <p className="mt-0.5 text-xs text-slate-600">
                Use your Meta catalogue with WhatsApp&apos;s native shopping experience.
              </p>
            ) : status.connected ? (
              <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-600">
                <span className="font-medium text-slate-800">{status.catalogName}</span>
                <span className="inline-flex items-center gap-1 text-emerald-700">
                  <CheckCircle2 size={13} /> Connected
                </span>
                <span>Last verified: {lastVerified}</span>
              </div>
            ) : (
              <p className="mt-0.5 text-xs text-slate-600">Choose an authorized Meta catalogue.</p>
            )}
          </div>
        </div>

        {!loaded ? (
          <Loader2 size={18} className="animate-spin text-slate-500" aria-label="Loading Meta catalogue connection" />
        ) : requiresReconnect ? (
          canConnect && (
            <button
              type="button"
              onClick={connect}
              disabled={Boolean(busy)}
              className="inline-flex h-9 items-center gap-2 rounded-md bg-emerald-700 px-3 text-sm font-semibold text-white disabled:opacity-50"
            >
              {busy === "connect" ? <Loader2 size={15} className="animate-spin" /> : <ExternalLink size={15} />}
              Reconnect Meta
            </button>
          )
        ) : needsSelection ? (
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={selectedId}
              onChange={event => setSelectedId(event.target.value)}
              disabled={busy === "catalogues" || catalogues.length === 0}
              aria-label="Meta catalogue"
              className="h-9 min-w-[220px] rounded-md border border-slate-300 bg-white px-2 text-sm"
            >
              {catalogues.length === 0 && <option value="">No eligible catalogues</option>}
              {catalogues.map(catalogue => (
                <option key={catalogue.id} value={catalogue.id}>{catalogue.name}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={select}
              disabled={!canConnect || !selectedId || Boolean(busy)}
              className="inline-flex h-9 items-center gap-2 rounded-md bg-emerald-700 px-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy === "select" && <Loader2 size={15} className="animate-spin" />}
              Connect catalogue
            </button>
          </div>
        ) : status.connected ? (
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setShowProducts(value => !value)} className="inline-flex h-9 items-center rounded-md border border-slate-300 px-3 text-sm font-semibold text-slate-700">
              {showProducts ? "Hide Meta Products" : "Manage Meta Products"}
            </button>
          {canDisconnect && (
            <button
              type="button"
              onClick={disconnect}
              disabled={Boolean(busy)}
              className="inline-flex h-9 items-center gap-2 rounded-md border border-red-200 px-3 text-sm font-semibold text-red-700 disabled:opacity-50"
            >
              {busy === "disconnect" && <Loader2 size={15} className="animate-spin" />}
              Disconnect
            </button>
          )}
          </div>
        ) : (
          canConnect && (
            <button
              type="button"
              onClick={connect}
              disabled={Boolean(busy)}
              className="inline-flex h-9 items-center gap-2 rounded-md bg-emerald-700 px-3 text-sm font-semibold text-white disabled:opacity-50"
            >
              {busy === "connect" ? <Loader2 size={15} className="animate-spin" /> : <ExternalLink size={15} />}
              Connect Meta Catalogue
            </button>
          )
        )}
      </div>

      {associationWarning && (
        <div className="mt-3 flex items-start gap-2 border-t border-amber-100 pt-3 text-xs text-amber-800">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <span>Catalogue access is connected, but its WhatsApp association could not be verified. Check WhatsApp Manager before using native presentation.</span>
        </div>
      )}
      {error && (
        <p
          role="alert"
          className={`mt-3 text-xs font-medium ${requiresReconnect || associationError ? "text-amber-800" : "text-red-700"}`}
        >
          {error}
        </p>
      )}
      {requiresReconnect && !error && (
        <p role="alert" className="mt-3 text-xs font-medium text-amber-800">
          Meta catalogue access is unavailable. Reconnect Meta to continue.
        </p>
      )}
      {needsSelection && busy !== "catalogues" && catalogues.length === 0 && !error && (
        <p className="mt-3 text-xs text-slate-600">No eligible catalogues were returned by Meta for this authorization.</p>
      )}
      {status.connected && (
        <p className="mt-2 text-[11px] text-slate-500">
          Disconnecting here does not delete your Meta catalogue. You can also revoke XploreByte in Meta Business settings.
        </p>
      )}
      {status.connected && showProducts && (
        <MetaCatalogProductsPanel
          canCreate={canConnect}
          canDelete={canDisconnect}
          onRevoked={loadStatus}
        />
      )}
    </section>
  );
}
